import { pxToEm } from 'util/pxToEm';
import { Box, Typography } from '@mui/material';
import { Image, Link, LinkProps, TruncateText } from 'components/lib';
import React, { ReactElement, useCallback } from 'react';
import { throttle } from 'lodash';
import { useRecoilState } from 'recoil';
import { Organization, useOrganizationsApi } from 'services/api';
import { isOrganizationFollowedState, markOrganizationAsUserModified } from 'state/followed-organizations/followed-organizations.state';
import { AddIcon } from 'components/icons';
import {
  colorSkeletonBackground,
  colorPrimaryNavy,
  colorLightGrey,
  colorLightBlueWhite,
  colorWhite,
} from 'customColors';

export interface OrganizationClickCardProps
  extends Omit<LinkProps, 'children' | 'as' | 'href' | 'onClick'> {
  logo?: string;
  title: string;
  id: Organization['id'];
  href?: string;
  onClick?: () => void;
  schoolSlug?: string;
}

export const OrganizationClickCard = ({
  logo,
  title,
  id,
  href,
  onClick,
  schoolSlug,
  ...linkProps
}: OrganizationClickCardProps): ReactElement => {
  const [isFollowed, setFollowed] = useRecoilState<boolean>(
    isOrganizationFollowedState(id),
  );
  const organizationsApi = useOrganizationsApi();

  const toggleFollowed = useCallback(
    throttle(
      async (isFollowed: boolean) => {
        // Mark this org as user-modified to prevent API data from overwriting
        markOrganizationAsUserModified(id);
        setFollowed(!isFollowed);
        if (isFollowed) await organizationsApi.unfollowOrganization(id);
        else await organizationsApi.followOrganization(id);
      },
      80,
      { trailing: false },
    ),
    [],
  );

  const handleClickFollow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void toggleFollowed(isFollowed);
  };

  const handleCardClick = (e?: React.MouseEvent) => {
    if (onClick) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      onClick();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        minWidth: 0,
        p: 16,
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 16,
        borderRadius: 8,
        border: `1px solid ${colorLightGrey}`,
        backgroundColor: colorLightBlueWhite,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
      }}
    >
      <Link
        href={onClick ? undefined : href}
        onClick={handleCardClick}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          textDecoration: 'none',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': {
            textDecoration: 'none',
          },
        }}
        {...linkProps}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              flexShrink: 0,
              borderRadius: 4,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colorSkeletonBackground,
            }}
          >
            {logo ? (
              <Image
                src={logo}
                alt={title}
                width="100%"
                height="100%"
                style={{
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: colorLightGrey,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: pxToEm(16),
                    fontWeight: 600,
                    color: colorPrimaryNavy,
                  }}
                >
                  {title.charAt(0).toUpperCase()}
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            component="button"
            onClick={handleClickFollow}
            sx={{
              display: 'flex',
              height: 50,
              px: 8,
              justifyContent: 'center',
              alignItems: 'center',
              gap: 4,
              borderRadius: 10,
              border: isFollowed ? 'none' : `1px solid ${colorPrimaryNavy}`,
              backgroundColor: isFollowed
                ? colorSkeletonBackground
                : colorWhite,
              color: colorPrimaryNavy,
              cursor: 'pointer',
              fontFamily: 'Poppins',
              fontSize: pxToEm(14),
              fontWeight: 500,
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease',
              outline: 'none',
              minWidth: isFollowed ? 78 : 'auto',
              '&:hover': {
                opacity: 0.85,
              },
              '&:focus-visible': {
                outline: `2px solid ${colorPrimaryNavy}`,
                outlineOffset: 2,
              },
            }}
          >
            {!isFollowed && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                }}
              >
                <AddIcon />
              </Box>
            )}
            <span
              style={{ whiteSpace: 'nowrap', fontWeight: 400, fontSize: 12 }}
            >
              {isFollowed ? 'Following' : 'Follow'}
            </span>
          </Box>
        </Box>

        <TruncateText lines={1}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: pxToEm(14),
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
        </TruncateText>
      </Link>
    </Box>
  );
};
